import { useRequiredContext } from "@redotech/react-util/context";
import { useDebounce } from "@redotech/react-util/debounce";
import { useLoad } from "@redotech/react-util/load";
import {
  ORIGIN_RETURN_TRACKING_TEAM_ID,
  SendEmailStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { getVisibleDynamicVariables } from "@redotech/redo-flows-common/schemas/filter-schema-fields";
import { schemas } from "@redotech/redo-flows-common/schemas/schemas";
import {
  CustomEventTriggerKey,
  isCoverageOrderLevelTrigger,
} from "@redotech/redo-flows-common/triggers";
import { useTranslation } from "@redotech/redo-i18n";
import EditPencilIcon from "@redotech/redo-icons/edit-05.svg";
import Mail01Icon from "@redotech/redo-icons/mail-01.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { TemplateType } from "@redotech/redo-marketing-templates-common/email-builder";
import { getDefaultTemplate } from "@redotech/redo-marketing-templates-common/email-template-defaults";
import { RedoMerchantEmailTemplatesRpcClientContext } from "@redotech/redo-merchant-app-common/email-templates-rpc-client-provider";
import { useEnabledIntegrations } from "@redotech/redo-merchant-app-common/hooks/use-enabled-integrations";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { Treatment } from "@redotech/redo-merchant-marketing-rpc/schema/automation-outreach-splits/get-automation-outreach-split";
import { EmailTextInputToolbar } from "@redotech/redo-web-email-builder/email-builder/builder/text-input-toolbar";
import { AiEmailPreviewSuggestions } from "@redotech/redo-web-marketing/marketing/components/ai-email-preview-suggestions";
import { AiSubjectLineSuggestions } from "@redotech/redo-web-marketing/marketing/components/ai-subject-line-suggestions";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Flex } from "@redotech/redo-web/flex";
import {
  InspectorField,
  InspectorLayout,
  InspectorSection,
} from "@redotech/redo-web/inspector-v2";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { Text } from "@redotech/redo-web/text";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PartialWithRequiredType, StepTypeDetailsProps } from ".";
import { InvalidStepsList } from "../components/invalid-steps-list";
import { AutomationAbTestEditor } from "./automation-ab-test-editor";
import { AutomationAbTestInspector } from "./automation-ab-test-inspector";
import { AutomationAbTestResultsPanel } from "./automation-ab-test-results-panel";
import { EmailPreviewContent } from "./common/email-preview-content";
import { useAbTestEditor } from "./common/use-ab-test-editor";
import { useDelayedAutoFocus } from "./common/use-delayed-auto-focus";
import { VariantPlaceholder } from "./common/variant-placeholders";

type Props = StepTypeDetailsProps<PartialWithRequiredType<SendEmailStep>>;

interface EmailDraft {
  emailName: string;
  subject: string;
  previewText: string;
}

/**
 * Module-level cache to persist draft email step state (emailName, subject,
 * previewText) by step ID. These fields live in local component state and
 * would otherwise be lost if the component remounts when navigating between
 * blocks of the same type.
 */
const emailDraftCache = new Map<string, EmailDraft>();

export function SendEmailTemplatePreview({
  step,
  schemaType,
  category,
  setStep,
  save,
  invalidSteps,
  setSelectedId,
  showValidationErrors,
  setAutomation,
  automationId,
  steps,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const team = useRequiredContext(TeamContext);
  const emailTemplatesRpcClient = useRequiredContext(
    RedoMerchantEmailTemplatesRpcClientContext,
  );
  const redoMerchantRpcClient = useRequiredContext(
    RedoMerchantRpcClientContext,
  );
  const otherInvalidSteps = invalidSteps?.filter((s) => s.id !== step.id) || [];
  const hasOtherInvalidSteps = !!otherInvalidSteps.length;

  // Thread the flow's custom-event trigger name into the builder URL so the
  // email preview can pull recent examples of that specific event.
  const customEventTrigger = steps?.find(
    (s) => "key" in s && s.key === CustomEventTriggerKey.CUSTOM_EVENT,
  );
  const customEventName =
    customEventTrigger &&
    "eventName" in customEventTrigger &&
    typeof customEventTrigger.eventName === "string"
      ? customEventTrigger.eventName
      : undefined;
  const templateType = steps?.some(
    (step) =>
      "key" in step &&
      typeof step.key === "string" &&
      isCoverageOrderLevelTrigger(step.key),
  )
    ? TemplateType.TRANSACTIONAL
    : TemplateType.MARKETING;
  const eventNameParam = customEventName
    ? `&eventName=${encodeURIComponent(customEventName)}`
    : "";

  // For advanced flow editor, we don't filter by integrations to keep all fields available
  const enabledIntegrations = useEnabledIntegrations();
  const availableDynamicVariables = useMemo(() => {
    const schema = schemaType ? schemas[schemaType] : undefined;
    return getVisibleDynamicVariables(schema, enabledIntegrations);
  }, [schemaType, enabledIntegrations]);

  const isOriginFlow = useMemo(() => {
    return location.pathname.includes("/managed/");
  }, [location.pathname]);

  const onOriginTeam = useMemo(() => {
    return team._id.toString() === ORIGIN_RETURN_TRACKING_TEAM_ID;
  }, [team]);

  const [emailName, setEmailName] = useState<string>(
    () => emailDraftCache.get(step.id)?.emailName ?? "",
  );
  const [subject, setSubject] = useState<string>(
    () => emailDraftCache.get(step.id)?.subject ?? "",
  );
  const [previewText, setPreviewText] = useState<string>(
    () => emailDraftCache.get(step.id)?.previewText ?? "",
  );
  const subjectLineRef = useRef(null);
  const previewTextRef = useRef(null);
  const [showFullEmailDialog, setShowFullEmailDialog] =
    useState<boolean>(false);
  const [showRevertModal, setShowRevertModal] = useState(false);
  const [emailNameTouched, setEmailNameTouched] = useState<boolean>(
    (showValidationErrors && !hasOtherInvalidSteps) || false,
  );
  const [emailTemplateReloadTrigger, setEmailTemplateReloadTrigger] =
    useState(Symbol());
  const [localName, setLocalName] = useState<string>("");
  const localNameForTemplateIdRef = useRef<string | undefined>(undefined);

  const delayedAutoFocus = useDelayedAutoFocus(!hasOtherInvalidSteps);
  const delayedAutoFocusInvalidSteps =
    useDelayedAutoFocus(hasOtherInvalidSteps);

  // Variant modal extra fields (email-specific)
  const [variantSubject, setVariantSubject] = useState("");
  const [variantPreviewText, setVariantPreviewText] = useState("");

  // Get the existing template and determine if it exists.
  // Return `undefined` (not null) when there is no templateId so that a
  // stale `undefined` value from a previous step with no template does NOT
  // satisfy the `=== null` "deleted" check in templateState below.
  const emailTemplateLoad = useLoad(
    async (signal) => {
      if (!step.templateId && !step.originTemplateId) {
        return undefined;
      }
      try {
        const input = step.templateId
          ? { id: step.templateId }
          : // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            { originTemplateId: step.originTemplateId! };
        return await emailTemplatesRpcClient.getEmailTemplate(input, {
          signal,
        });
      } catch {
        return null;
      }
    },
    [
      step.templateId,
      step.originTemplateId,
      emailTemplateReloadTrigger,
      emailTemplatesRpcClient,
    ],
  );

  const templateState = useMemo(() => {
    if (step.originTemplateId && !step.templateId && emailTemplateLoad.value) {
      const isOriginTemplate =
        emailTemplateLoad.value.isOrigin && !onOriginTeam;
      return {
        state: isOriginTemplate ? ("origin" as const) : ("exists" as const),
        template: emailTemplateLoad.value,
      };
    }
    if (step.templateId && emailTemplateLoad.value) {
      return { state: "exists" as const, template: emailTemplateLoad.value };
    }
    if (
      (step.templateId || step.originTemplateId) &&
      emailTemplateLoad.value === null
    ) {
      return { state: "deleted" as const };
    }
    return { state: "create" as const };
  }, [
    step.templateId,
    step.originTemplateId,
    emailTemplateLoad.value,
    onOriginTeam,
  ]);

  useEffect(() => {
    if (templateState.state === "exists") {
      const id = templateState.template._id.toString();
      if (id !== localNameForTemplateIdRef.current) {
        setLocalName(templateState.template.name);
        localNameForTemplateIdRef.current = id;
      }
    }
  }, [templateState]);

  const debouncedName = useDebounce(localName, 800);

  useEffect(() => {
    if (templateState.state !== "exists" || !debouncedName) {
      return;
    }
    if (debouncedName === templateState.template.name) {
      return;
    }
    emailTemplatesRpcClient
      .updateEmailTemplate({
        emailTemplateId: templateState.template._id.toString(),
        updates: { name: debouncedName },
      })
      .catch((error) => {
        const message =
          error instanceof Error ? error.message : t("Unknown error");
        toast(t("Error saving email name: {{message}}", { message }), {
          variant: "error",
        });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedName]);

  // Create template function for A/B test variants
  const createVariantTemplate = async (name: string) => {
    const newTemplate = getDefaultTemplate({
      team,
      name,
      subject: variantSubject,
      emailPreview: variantPreviewText,
      type: templateType,
      schemaType,
      category,
    });
    const response =
      await emailTemplatesRpcClient.createEmailTemplate(newTemplate);
    return response._id.toString();
  };

  // A/B test editor hook
  const abTest = useAbTestEditor({
    automationId,
    stepId: step.id,
    stepType: "send_email",
    splitId: step.splitId,
    existingTemplateId: step.templateId,
    existingTemplateName:
      templateState.state === "exists" || templateState.state === "origin"
        ? templateState.template.name
        : undefined,
    onSplitIdChange: (splitId) => setStep({ ...step, splitId }),
    onSave: save,
    createTemplate: createVariantTemplate,
    getEditPath: (templateId, returnPath) =>
      `../../email-sms/templates/email/${templateId}?${returnPath}&showEmailReferencesModal=true${eventNameParam}`,
  });

  const renderVariantPreview = (treatment: Treatment) => {
    if (!treatment.templateId) {
      return <VariantPlaceholder hasTemplate={false} type="template" />;
    }
    return <EmailPreviewContent templateId={treatment.templateId} />;
  };

  const renderFullVariantPreview = (treatment: Treatment) => {
    if (!treatment.templateId) {
      return (
        <Flex align="center" justify="center" p="lg" w="full">
          <Text fontSize="sm" textAlign="center" textColor="secondary">
            {t("No template selected")}
          </Text>
        </Flex>
      );
    }
    return (
      <EmailPreviewContent fullPreview templateId={treatment.templateId} />
    );
  };

  // Handle opening variant modal - reset extra fields
  const handleOpenVariantModal = () => {
    setVariantSubject("");
    setVariantPreviewText("");
    const variantLetter = String.fromCharCode(
      65 + (abTest.splitWithTreatments?.treatments.length ?? 1),
    );
    const variantName = `Variant ${variantLetter}`;
    abTest.variantModal.open(
      variantName,
      t("{{variantName}} Email", { variantName }),
    );
  };

  const emailNameError = useMemo(() => {
    if (
      templateState.state !== "exists" &&
      templateState.state !== "origin" &&
      !emailName &&
      emailNameTouched
    ) {
      return t("Email name is required");
    }
    return undefined;
  }, [emailName, templateState.state, emailNameTouched, t]);

  // When the selected step changes (step.id), save the current draft to the
  // cache so it can be restored if this step is selected again.
  useEffect(
    () =>
      function setDraftCacheOnStepChange() {
        // Save draft only while the step is still in "create" mode.
        // Once a template exists the draft fields are irrelevant.
        if (!step.templateId && !step.originTemplateId) {
          emailDraftCache.set(step.id, { emailName, subject, previewText });
        } else {
          emailDraftCache.delete(step.id);
        }
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [step.id],
  );

  useEffect(
    function removeIdOfDeletedTemplate() {
      if (templateState.state === "deleted") {
        setStep({ ...step, templateId: undefined });
      }
    },
    [templateState, setStep, step],
  );

  const handleCreateTemplateClick = async () => {
    try {
      const newTemplate = getDefaultTemplate({
        team,
        name: emailName,
        subject,
        emailPreview: previewText,
        type: templateType,
        schemaType,
        category,
      });

      const response =
        await emailTemplatesRpcClient.createEmailTemplate(newTemplate);

      const updatedStep = {
        ...step,
        templateId: response._id.toString(),
        originTemplateId: undefined,
      };

      if (isOriginFlow && automationId && setAutomation) {
        const currentFlowData = await redoMerchantRpcClient.getAdvancedFlowById(
          { flowId: automationId },
        );

        if (!currentFlowData.advancedFlow) {
          throw new Error("Origin flow not loaded");
        }

        const { _id, createdAt, updatedAt, versionGroupId, ...flowData } =
          currentFlowData.advancedFlow;

        const updatedSteps = steps.map((s) =>
          s.id === step.id ? updatedStep : s,
        );

        const newFlowId = await redoMerchantRpcClient.createAdvancedFlow({
          newFlow: { ...flowData, steps: updatedSteps as any, team: team._id },
          setIndex: true,
        });

        setAutomation((prev) =>
          prev ? { ...prev, _id: newFlowId.id, steps: updatedSteps } : prev,
        );

        const newPath = location.pathname.replace(
          `/managed/${automationId}`,
          `/${newFlowId.id}`,
        );

        navigate(
          `../../email-sms/templates/email/${response._id.toString()}?returnPath=${encodeURIComponent(newPath)}&selected=${step.id}&showEmailReferencesModal=true${eventNameParam}`,
          { replace: true },
        );

        // Draft is no longer needed once the template is created.
        emailDraftCache.delete(step.id);

        toast(t("Flow customized and saved to your automations"), {
          variant: "success",
        });
      } else {
        setStep(updatedStep);
        save?.();
        // Draft is no longer needed once the template is created.
        emailDraftCache.delete(step.id);

        navigate(
          `../../email-sms/templates/email/${response._id.toString()}?returnPath=${encodeURIComponent(location.pathname)}&selected=${step.id}&showEmailReferencesModal=true${eventNameParam}`,
        );
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("Unknown error");
      toast(t("Error creating template: {{message}}", { message }), {
        variant: "error",
      });
    }
  };

  const handleRevertToManagedEmail = async () => {
    if (!step.originTemplateId) {
      toast(t("No origin template found"), { variant: "error" });
      return;
    }

    try {
      await emailTemplatesRpcClient.deleteOriginTemplateFromTeam({
        originTemplateId: step.originTemplateId,
      });
      setShowRevertModal(false);
      setEmailTemplateReloadTrigger(Symbol());

      toast(t("Reverted to default email template"), { variant: "success" });
    } catch (error) {
      const message =
        error instanceof Error ? error.message : t("Unknown error");
      toast(t("Error reverting to default email: {{message}}", { message }), {
        variant: "error",
      });
    }
  };

  const handleEditTemplate = () => {
    const returnPath = `returnPath=${encodeURIComponent(location.pathname)}&selected=${step.id}`;
    const isOriginTemplate = templateState.state === "origin";

    if (isOriginTemplate) {
      if (step.originTemplateId) {
        navigate(
          `../../email-sms/templates/email/managed/${step.originTemplateId}?${returnPath}${eventNameParam}`,
        );
      }
    } else {
      const templateIdToEdit =
        step.templateId ||
        (templateState.state === "exists"
          ? templateState.template._id.toString()
          : undefined);
      if (templateIdToEdit) {
        navigate(
          `../../email-sms/templates/email/${templateIdToEdit}?${returnPath}${eventNameParam}`,
        );
      }
    }
  };

  const handleSelectWinner = (winningTemplateId: string) => {
    setStep({
      ...step,
      templateId: winningTemplateId,
      originTemplateId: undefined,
      splitId: undefined,
    });
    save?.();
    abTest.refetch();
  };

  // Render based on A/B test view
  if (abTest.view === "loading") {
    return (
      <Flex align="center" grow={1} justify="center">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  if (abTest.view === "results" && automationId) {
    return (
      <AutomationAbTestResultsPanel
        automationId={automationId}
        automationStepId={step.id}
        icon={<Mail01Icon height={20} width={20} />}
        onSelectWinner={handleSelectWinner}
        renderFullVariantPreview={renderFullVariantPreview}
        renderVariantPreview={renderVariantPreview}
        stepType="send_email"
        title={t("Send email A/B test")}
      />
    );
  }

  if (abTest.view === "editor" && abTest.splitWithTreatments) {
    return (
      <>
        <AutomationAbTestEditor
          icon={<Mail01Icon height={20} width={20} />}
          onAddVariant={handleOpenVariantModal}
          onClose={abTest.closeEditor}
          onEditVariant={abTest.editVariant}
          renderVariantPreview={renderVariantPreview}
          splitWithTreatments={abTest.splitWithTreatments}
          stepType="send_email"
          title={t("Send email A/B test")}
        />

        {/* Variant template creation modal */}
        <RedoModal
          isOpen={abTest.variantModal.isOpen}
          modalSize="md"
          onModalCloseRequested={abTest.variantModal.close}
          primaryButton={{
            text: t("Create Template"),
            onClick: abTest.variantModal.create,
            disabled: !abTest.variantModal.templateName,
          }}
          secondaryButton={{
            text: t("Cancel"),
            onClick: abTest.variantModal.close,
          }}
          title={t("Create template for {{variantName}}", {
            variantName: abTest.variantModal.variantName,
          })}
        >
          <Flex dir="column" gap="lg">
            <Flex dir="column" gap="xs">
              <Text fontSize="xs" textColor="secondary">
                {t("Email name")}
              </Text>
              <RedoTextInput
                placeholder={t("Enter email name")}
                setValue={abTest.variantModal.setTemplateName}
                size="sm"
                value={abTest.variantModal.templateName}
              />
            </Flex>

            <Flex dir="column" gap="xs">
              <Text fontSize="xs" textColor="secondary">
                {t("Subject line")}
              </Text>
              <RedoTextInput
                placeholder={t("Enter subject line")}
                setValue={setVariantSubject}
                size="sm"
                value={variantSubject}
              />
            </Flex>

            <Flex dir="column" gap="xs">
              <Text fontSize="xs" textColor="secondary">
                {t("Preview text")}
              </Text>
              <RedoTextInput
                placeholder={t("Enter preview text")}
                setValue={setVariantPreviewText}
                size="sm"
                value={variantPreviewText}
              />
            </Flex>
          </Flex>
        </RedoModal>
      </>
    );
  }

  // Loading states for template data
  if (emailTemplateLoad.pending) {
    return (
      <Flex align="center" grow={1} justify="center">
        <LoadingRedoAnimation />
      </Flex>
    );
  }

  const isReadOnly =
    templateState.state === "exists" || templateState.state === "origin";
  const isOriginTemplate = templateState.state === "origin";
  const template =
    templateState.state === "exists" || templateState.state === "origin"
      ? templateState.template
      : undefined;

  const hasTemplate = !!step.templateId || !!step.originTemplateId;

  // Normal inspector view
  return (
    <InspectorLayout title={t("Send email")}>
      <InspectorSection label={t("Email")}>
        <InspectorField label={t("Name")}>
          <Flex dir="column" gap="xs" w="full">
            <Flex align="center" gap="sm" justify="space-between" w="full">
              <Flex grow={1}>
                <RedoTextInput
                  autoFocus={delayedAutoFocus}
                  errorText={emailNameError}
                  fullWidth
                  key={delayedAutoFocus.toString()}
                  onBlur={() => setEmailNameTouched(true)}
                  placeholder={
                    isOriginTemplate || hasOtherInvalidSteps
                      ? t("No name")
                      : t("Add a name")
                  }
                  setValue={
                    templateState.state === "exists"
                      ? setLocalName
                      : setEmailName
                  }
                  size="sm"
                  state={
                    isOriginTemplate || hasOtherInvalidSteps
                      ? "readonly"
                      : "default"
                  }
                  value={
                    templateState.state === "exists"
                      ? localName
                      : template?.name || emailName
                  }
                />
              </Flex>
              {isOriginTemplate && (
                <RedoBadge
                  color="gray"
                  size="xs"
                  text={t("Default template")}
                />
              )}
            </Flex>
          </Flex>
        </InspectorField>

        <InspectorField label={t("Subject")}>
          <Flex dir="column" gap="xs" w="full">
            <RedoTextareaInputField
              autoExpand
              placeholder={
                isReadOnly ? t("No subject line") : t("Add a subject")
              }
              readOnly={isReadOnly || hasOtherInvalidSteps}
              ref={subjectLineRef}
              setValue={setSubject}
              toolbar={
                <EmailTextInputToolbar
                  availableDynamicVariables={availableDynamicVariables}
                  inputRef={subjectLineRef}
                  schemaType={schemaType}
                  setText={setSubject}
                  text={subject}
                />
              }
              value={template?.subject || subject}
            />
            {!isReadOnly && !hasOtherInvalidSteps && (
              <AiSubjectLineSuggestions
                getAiSubjectLineSuggestions={
                  emailTemplatesRpcClient.getAiSubjectLineSuggestions
                }
                onSuggestionClick={setSubject}
                subject={subject}
              />
            )}
          </Flex>
        </InspectorField>

        <InspectorField label={t("Preview")}>
          <Flex dir="column" gap="xs" w="full">
            <RedoTextareaInputField
              autoExpand
              placeholder={
                isReadOnly ? t("No preview text") : t("Add a preview")
              }
              readOnly={isReadOnly || hasOtherInvalidSteps}
              ref={previewTextRef}
              setValue={setPreviewText}
              toolbar={
                <EmailTextInputToolbar
                  availableDynamicVariables={availableDynamicVariables}
                  inputRef={previewTextRef}
                  schemaType={schemaType}
                  setText={setPreviewText}
                  text={previewText}
                />
              }
              value={template?.emailPreview || previewText}
            />
            {!isReadOnly && !hasOtherInvalidSteps && (
              <AiEmailPreviewSuggestions
                emailPreview={previewText}
                getAiEmailPreviewSuggestions={
                  emailTemplatesRpcClient.getAiEmailPreviewSuggestions
                }
                onSuggestionClick={setPreviewText}
                subjectLine={subject}
              />
            )}
          </Flex>
        </InspectorField>

        <Flex dir="column" gap="xs" w="full">
          <Flex align="center" justify="space-between" w="full">
            <Text fontSize="xs" textColor="secondary">
              {t("Content")}
            </Text>
            {isReadOnly && (
              <RedoButton
                disabled={hasOtherInvalidSteps}
                hierarchy="secondary"
                IconLeading={EditPencilIcon}
                onClick={handleEditTemplate}
                size="xs"
                text={isOriginTemplate ? t("Customize") : t("Edit")}
              />
            )}
          </Flex>

          <Flex
            bgColor="primary"
            borderColor={
              !isReadOnly &&
              !(step.templateId || step.originTemplateId) &&
              showValidationErrors
                ? "error"
                : "primary"
            }
            borderRadius="md"
            borderStyle="solid"
            borderWidth="1px"
            dir="column"
            p="sm"
            position="relative"
            style={{ maxHeight: "400px" }}
            w="full"
          >
            {hasOtherInvalidSteps && setSelectedId ? (
              <InvalidStepsList
                autoFocus={delayedAutoFocusInvalidSteps}
                invalidSteps={otherInvalidSteps}
                key={delayedAutoFocusInvalidSteps.toString()}
                onGoToStep={setSelectedId}
              />
            ) : step.templateId || step.originTemplateId ? (
              <EmailPreviewContent
                onClick={() => setShowFullEmailDialog(true)}
                originTemplateId={step.originTemplateId}
                templateId={step.templateId}
              />
            ) : isReadOnly ? (
              <Flex
                align="center"
                bgColor="primary"
                height="half-screen"
                justify="center"
              >
                <Text fontSize="xs" textAlign="center" textColor="secondary">
                  {t("Could not generate preview")}
                </Text>
              </Flex>
            ) : (
              <Flex
                align="center"
                bgColor="primary"
                height="half-screen"
                justify="center"
              >
                <RedoButton
                  disabled={!emailName}
                  hierarchy="secondary"
                  IconLeading={PlusIcon}
                  onClick={handleCreateTemplateClick}
                  text={t("Content")}
                  theme="action"
                />
              </Flex>
            )}
          </Flex>

          {!isReadOnly &&
            !(step.templateId || step.originTemplateId) &&
            showValidationErrors && (
              <Text fontSize="xs" textColor="error">
                {hasOtherInvalidSteps
                  ? t("Fix invalid steps before adding content", {
                      count: otherInvalidSteps.length,
                    })
                  : !emailName
                    ? t("Add email name before adding content")
                    : t("Content is required")}
              </Text>
            )}
        </Flex>
      </InspectorSection>

      <InspectorSection label={t("Step")}>
        <InspectorField label={null}>
          <RedoToggle
            label={t("Deactivate")}
            setValue={(disabled) => setStep({ ...step, disabled })}
            size="sm"
            value={step.disabled ?? false}
          />
        </InspectorField>

        {step.originTemplateId && !isOriginTemplate && !onOriginTeam && (
          <RedoButton
            disabled={hasOtherInvalidSteps}
            hierarchy="secondary"
            onClick={() => setShowRevertModal(true)}
            size="sm"
            text={t("Revert to default email")}
            theme="destructive"
          />
        )}
      </InspectorSection>

      {/* A/B test panel */}
      {automationId && (
        <AutomationAbTestInspector
          existingTest={abTest.splitWithTreatments}
          hasTemplate={hasTemplate}
          onOpenEditor={abTest.openEditor}
        />
      )}

      {showFullEmailDialog && (step.templateId || step.originTemplateId) && (
        <RedoModal
          isOpen={showFullEmailDialog}
          modalSize="lg"
          onModalCloseRequested={() => setShowFullEmailDialog(false)}
          primaryButton={{
            text: isOriginTemplate ? t("Customize") : t("Edit"),
            onClick: handleEditTemplate,
          }}
          secondaryButton={{
            text: t("Close"),
            onClick: () => setShowFullEmailDialog(false),
          }}
          title={t("Email Preview")}
        >
          <Flex
            dir="column"
            p="lg"
            style={{ maxHeight: "70vh", overflow: "auto" }}
          >
            <EmailPreviewContent
              fullPreview
              originTemplateId={step.originTemplateId}
              templateId={step.templateId}
            />
          </Flex>
        </RedoModal>
      )}

      {showRevertModal && (
        <RedoModal
          isOpen={showRevertModal}
          onModalCloseRequested={() => setShowRevertModal(false)}
          primaryButton={{
            text: t("Revert"),
            onClick: handleRevertToManagedEmail,
            theme: "destructive",
          }}
          secondaryButton={{
            text: t("Cancel"),
            onClick: () => setShowRevertModal(false),
          }}
          theme="error"
          title={t("Revert to default email?")}
        >
          <Flex dir="column" gap="md">
            <Text fontSize="sm" textColor="secondary">
              {t(
                "This will discard your custom email template and revert to the default version. This action cannot be undone.",
              )}
            </Text>
          </Flex>
        </RedoModal>
      )}
    </InspectorLayout>
  );
}
